import React, { useState, useEffect } from "react";
import OAuthCallback from './pages/Oauth-callback.page/Oauth-callback.page';
import { useAuthentication } from './global/authentication';
import { getUser } from './services/AuthService';
import App from './App'; // Import the App component

// Create a wrapper component to use the hook and pass setIsAuthenticated
function OAuthCallbackWithAuth(props) {
    const { setAuthentication } = useAuthentication(); // Get setAuthentication from the context
    return <OAuthCallback {...props} setIsAuthenticated={setAuthentication} />;
}

const RoutesComponent = () => {
    const { isAuthenticated, principal, setAuthentication } = useAuthentication(); // Get authenticated state and setAuthentication from AuthenticationContext
    const [loading, setLoading] = useState(true); // Add loading state

    const fetchUser = async () => {
        const _user = await getUser();
        if (_user) { // Check if user is not null or undefined
            setAuthentication({
                isAuthenticated: true,
                principal: _user,
            }); // Set the user state
        }
        else {
            setAuthentication({
                isAuthenticated: false,
                principal: _user,
            }); // Set the user state
        }
        setLoading(false); // Set loading to false after fetching user
    };

    useEffect(() => {
        fetchUser();
    }, []); // Empty dependency array to run only once on mount

    useEffect(() => {
        // This effect will run whenever the authenticated state changes
        if (isAuthenticated && principal) {
            // Perform any necessary actions when the principal is updated
            console.log("Principal updated:", principal);
        }
    }, [isAuthenticated, principal]); // Dependency array includes authenticated

    if (loading) {
        return <div>Loading...</div>; // Render loading indicator while fetching user
    }

    const roles = principal && principal.profile && Array.isArray(principal.profile.role) ? principal.profile.role : [principal?.profile?.role].filter(Boolean);

    const isAdminOrPm = isAuthenticated && principal && principal.profile && (roles.includes('admin') || roles.includes('pm'));
    const isAdmin = isAuthenticated && principal && principal.profile && roles.includes('admin');


    const routes = {
        key: "home",
        name: "Home",
        icon: "Home",
        path: "/",
        component: React.lazy(() => import("./pages/Dashboard")),
        children: [
            {
                key: "login",
                name: "Login",
                isPublic: true,
                isHidden: true,
                component: React.lazy(() => import("./pages/Login")),
            },
            {
                key: "oauth-callback",  // Adding OAuth callback route
                name: "OAuth Callback",
                isPublic: true,
                isElementVisible: false,
                isHidden: true,
                path: "/oauth/callback",  // This will match the /oauth/callback URL
                component: OAuthCallbackWithAuth // Handle callback
            },
            {
                key: "logout",  // Adding OAuth callback route
                name: "Logout",
                isPublic: false,
                isElementVisible: false,
                path: "/logout-success",  // This will match the /oauth/callback URL
                component: React.lazy(() => import("./pages/Logout")),
            },
            {
                key: "calendar",
                name: "Calendar",
                icon: "Calendar",
                component: React.lazy(() => import("./pages/Calendar")),
            },
            {
                key: "profile",
                name: "profile",
                isElementVisible: false,
                isHidden: false,
                icon: "Profile",
                component: React.lazy(() => import("./pages/UserProfile")),
            },
            isAdmin && { // Conditionally render the Report menu item
                key: "report",
                name: "Report",
                icon: "ReportDocument",
                component: React.lazy(() => import("./pages/Report")), // Add the Report component
            },
            isAdminOrPm && { // Conditionally render the Project View menu item
                key: "projectView",
                name: "Project View",
                icon: "ReportDocument",
                component: React.lazy(() => import("./pages/Projects")), // Add the Project View component
            },
            isAdmin && {
                key: "management",
                name: "System Management",
                icon: "managementApp",
                children: [
                    {
                        key: "organization",
                        name: "Organization",
                        icon: "Org",
                    },
                    {
                        key: "user",
                        name: "Users",
                        icon: "People",
                        component: React.lazy(() => import("./pages/Users")), // Add the User component
                    },
                    {
                        key: "authority",
                        name: "Authority",
                        icon: "SecurityGroup",
                    },
                    {
                        key: "settings",
                        name: "Settings",
                        icon: "Settings",
                        children: [
                            {
                                key: "smtp",
                                name: "SMTP",
                                icon: "Mail",
                                component: React.lazy(() => import("./pages/SmtpSettings")), // Add the SMTP settings component
                            },
                            {
                                key: "imap",
                                name: "IMAP",
                                icon: "Inbox",
                                component: React.lazy(() => import("./pages/ImapSettings")), // Add the IMAP settings component
                            },
                            {
                                key: "chatbot",
                                name: "Chatbot",
                                icon: "Robot",
                                component: React.lazy(() => import("./pages/ChatbotSettings")), // Add the Chatbot settings component
                            },
                            {
                                key: "list",
                                name: "List",
                            },
                            {
                                key: "unit",
                                name: "Unit",
                            },
                        ],
                    },
                ],
            },
        ].filter(Boolean), // Filter out any false values
    };

    return (
        <App routes={routes} isAuthenticated={isAuthenticated} />
    );
};

export default RoutesComponent;
